import { useState, type FormEvent } from "react";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { describePasswordChangeError, hasPasswordProvider, validateNewPassword } from "@/lib/passwordChange";

interface ChangePasswordSectionProps {
  className?: string;
  /** "gate" is the first-login interstitial: same form, different framing copy. */
  variant?: "card" | "gate";
  onChanged?: () => void;
}

// WP4 (audit pwreset-1): the ONLY in-app place a password can be changed. Mounted
// as an Account card on both Settings pages and full-screen by
// ForcePasswordChangeGate. Google-only accounts (D8: every BiG team member) see a
// note instead of a form.
export function ChangePasswordSection({ className, variant = "card", onChanged }: ChangePasswordSectionProps) {
  const { user, realRole, refreshUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  const providerIds = user.providerData.map((p) => p.providerId);
  const isPlatform = realRole === "PLATFORM_OWNER" || realRole === "PLATFORM_ADMIN";
  const card = cn("rounded-lg border border-border bg-white p-6", className);

  if (!hasPasswordProvider(providerIds)) {
    return (
      <div className={card}>
        <h2 className="font-heading text-lg font-semibold text-text">Account</h2>
        <p className="mt-1 font-body text-sm text-text-muted">
          You sign in with Google as <span className="text-text">{user.email}</span>. There is no Studio Manager
          password to manage.
        </p>
        {isPlatform && (
          <p className="mt-2 font-body text-sm text-text-muted">Big Impact Group team members sign in with Google.</p>
        )}
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user?.email) return;
    setError(null);
    setSuccess(false);
    const problem = validateNewPassword(next, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      // Allowed by the orgUsers self-update rule (WP4). Best-effort: the password
      // is already changed, so a rules lag must not turn into an error here.
      await updateDoc(doc(db, "orgUsers", user.uid), {
        mustChangePassword: false,
        passwordChangedAt: serverTimestamp(),
      }).catch(() => {});
      setCurrent("");
      setNext("");
      setConfirm("");
      setSuccess(true);
      await refreshUser();
      onChanged?.();
    } catch (err) {
      setError(describePasswordChangeError(err));
    } finally {
      setSaving(false);
    }
  }

  const inputClass = "w-full";
  return (
    <div className={card}>
      <h2 className="font-heading text-lg font-semibold text-text">
        {variant === "gate" ? "Choose your password" : "Account"}
      </h2>
      <p className="mb-4 mt-1 font-body text-sm text-text-muted">
        {variant === "gate"
          ? "Your invitation came with a temporary password. Pick your own to continue."
          : `Signed in as ${user.email}. Change your password here; you will stay signed in.`}
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="current-password" className="mb-1 block font-body text-sm font-medium text-text">
            Current password
          </label>
          <Input id="current-password" type="password" autoComplete="current-password" required className={inputClass} value={current} onChange={(e) => setCurrent(e.target.value)} disabled={saving} />
        </div>
        <div>
          <label htmlFor="new-password" className="mb-1 block font-body text-sm font-medium text-text">
            New password
          </label>
          <Input id="new-password" type="password" autoComplete="new-password" required minLength={8} className={inputClass} value={next} onChange={(e) => setNext(e.target.value)} disabled={saving} />
        </div>
        <div>
          <label htmlFor="confirm-password" className="mb-1 block font-body text-sm font-medium text-text">
            Confirm new password
          </label>
          <Input id="confirm-password" type="password" autoComplete="new-password" required minLength={8} className={inputClass} value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={saving} />
        </div>
        {error && <div role="alert" className="rounded-md bg-red-50 p-3 font-body text-sm text-red-700">{error}</div>}
        {success && <div role="status" className="rounded-md bg-emerald-50 p-3 font-body text-sm text-emerald-700">Password changed.</div>}
        <Button type="submit" disabled={saving}>
          {saving ? "Saving..." : variant === "gate" ? "Save and continue" : "Change password"}
        </Button>
      </form>
    </div>
  );
}
